import { DocumentProviderType } from 'models/documentProvider';

export enum DocumentConnectionStatus {
  Disabled = 0,
  Enabled = 1,
}

/** Mirrored by numeric value from the API (DocumentConnection.DocumentConnectionAuthModes). */
export enum DocumentConnectionAuthMode {
  /** The credential stored on the connection: a service account key, an app password, an integration token. */
  TenantCredential = 0,
  /** Google Drive only: REDLAB's delegated service account acts as the signed-in Workspace user; no secret on the row. */
  DomainDelegated = 1,
  /** Nextcloud: the row holds the OAuth2 client; each user grants their own account once (a DocumentConnectionGrant). */
  UserDelegated = 2,
}

/**
 * A tenant-level connection to a document provider. The credentials never leave the server:
 * authData is write-only on the way out, and hasAuthData is how the UI knows a secret is
 * already stored.
 */
export default class DocumentConnection {
  documentConnectionId: number;

  providerType: DocumentProviderType;

  name: string;

  status: DocumentConnectionStatus;

  authMode: DocumentConnectionAuthMode;

  isAuthorized?: boolean;

  /** Non-secret provider configuration as JSON. */
  config?: string;

  /** Only ever sent TO the server: filled when the admin enters new credentials. */
  authData?: string;

  hasAuthData: boolean;

  lastTestedDate?: Date;

  created?: Date;

  createdById?: string;

  constructor() {
    this.documentConnectionId = -1;
    this.providerType = DocumentProviderType.GoogleDrive;
    this.name = '';
    this.status = DocumentConnectionStatus.Enabled;
    this.authMode = DocumentConnectionAuthMode.TenantCredential;
    this.hasAuthData = false;
  }

  isEqual(item: DocumentConnection): boolean {
    if (item.documentConnectionId !== this.documentConnectionId) return false;
    if (item.providerType !== this.providerType) return false;
    if (item.name !== this.name) return false;
    if (item.status !== this.status) return false;
    if (item.authMode !== this.authMode) return false;
    if (item.config !== this.config) return false;
    // a newly typed secret is a change even though the stored one is never returned
    if (item.authData !== this.authData) return false;

    return true;
  }

  clone = (): DocumentConnection => {
    const output = new DocumentConnection();

    output.documentConnectionId = this.documentConnectionId;
    output.providerType = this.providerType;
    output.name = this.name;
    output.status = this.status;
    output.authMode = this.authMode;
    output.isAuthorized = this.isAuthorized;
    output.config = this.config;
    output.authData = this.authData;
    output.hasAuthData = this.hasAuthData;
    output.lastTestedDate = this.lastTestedDate;
    output.created = this.created;
    output.createdById = this.createdById;

    return output;
  };
}

/**
 * The signed-in user's own grant on a user-delegated connection. The tokens stay on the server;
 * this is what the "Connected accounts" list shows and what a Disconnect removes.
 */
export class DocumentConnectionGrant {
  documentConnectionId: number;

  connectionName: string;

  providerType: DocumentProviderType;

  /** False once the provider refused the stored tokens; the user connects again from the category. */
  isAuthorized?: boolean;

  lastTestedDate?: Date;

  created?: Date;

  constructor() {
    this.documentConnectionId = -1;
    this.connectionName = '';
    this.providerType = DocumentProviderType.Nextcloud;
  }
}

export class DocumentConnectionTestResult {
  success: boolean;

  message?: string;

  /** The DOCPROV code when the provider refused the test, so the failure can be said in the user's language. */
  code?: string;

  accountDisplay?: string;

  constructor() {
    this.success = false;
  }
}

/** One place to browse into at a provider: a shared drive, a files root, a workspace. */
export class ProviderContainer {
  id: string;

  name: string;

  kind?: string;

  hasChildren: boolean;

  /**
   * False when the container is a browsing device rather than a place (Google's "shared with
   * the service account"), so a category has to be bound to a folder inside it.
   */
  canBeRoot: boolean;

  constructor() {
    this.id = '';
    this.name = '';
    this.hasChildren = false;
    this.canBeRoot = true;
  }
}

export class ProviderItem {
  id: string;

  name: string;

  /** Pass back as folderId to descend; null for providers addressed by id. */
  browsePath?: string;

  isFolder: boolean;

  mimeType?: string;

  size?: number;

  modified?: Date;

  webUrl?: string;

  canDownload: boolean;

  constructor() {
    this.id = '';
    this.name = '';
    this.isFolder = false;
    this.canDownload = false;
  }
}

export class ProviderItemPage {
  items: ProviderItem[];

  nextPageToken?: string;

  constructor() {
    this.items = [];
  }
}

export class ProviderItemMetadata {
  linkId: number;

  itemId?: string;

  name?: string;

  mimeType?: string;

  size?: number;

  lastModified?: Date;

  webUrl?: string;

  /** This one item could not be read; the rest of the batch is still valid. */
  hasError: boolean;

  constructor() {
    this.linkId = -1;
    this.hasError = false;
  }
}

export class ProviderPageContent {
  title?: string;

  markdown?: string;

  html?: string;

  lastModified?: Date;

  webUrl?: string;
}
